import { Button } from '@design/components/button/Button';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { useFollowedTopics } from '@terminal/react-query-hooks/topics/useFollowedTopics';
import { useFollowTopic } from '@terminal/react-query-hooks/topics/useFollowTopic';
import { useUnfollowTopic } from '@terminal/react-query-hooks/topics/useUnfollowTopic';
import type { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import clsx from 'clsx';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './FollowThemeButton.module.scss';

export interface FollowThemeButtonProps {
  themeId: string;
  className?: string;
  buttonTestId?: string;
  trackingSource?: PosthogTrackingSource;
  variant?: 'default' | 'icon';
}

export const FollowThemeButton = ({
  themeId,
  className,
  buttonTestId = 'follow-theme-button',
  trackingSource,
  variant = 'default',
}: FollowThemeButtonProps) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const { data } = useFollowedTopics('theme');
  const { mutate: followTopic, isPending: isFollowingPending } =
    useFollowTopic();
  const { mutate: unfollowTopic, isPending: isUnfollowingPending } =
    useUnfollowTopic();
  const { trackFollowTheme, trackUnfollowTheme } = usePosthogEvents();

  const isFollowed = data?.values.includes(themeId) ?? false;
  const isPending = isFollowingPending || isUnfollowingPending;
  const isIcon = variant === 'icon';
  const label = isFollowed
    ? t('common:components.followThemeBtn.followingLabel')
    : t('common:components.followThemeBtn.followThemeLabel');

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isFollowed) {
      unfollowTopic({ type: 'theme', code: themeId });
      if (trackingSource) {
        trackUnfollowTheme(trackingSource, themeId);
      }
    } else {
      followTopic({ type: 'theme', code: themeId });
      if (trackingSource) {
        trackFollowTheme(trackingSource, themeId);
      }
    }
  };

  const button = (
    <Button
      className={clsx(
        styles.followThemeButton,
        { [styles.active]: isFollowed, [styles.icon]: isIcon },
        className
      )}
      aria-label={label}
      data-testid={buttonTestId}
      disabled={isPending}
      iconName={isFollowed ? 'Checkmark' : 'Add'}
      label={isIcon ? undefined : label}
      onClick={handleClick}
      size={isIcon ? 'xsmall' : 'small'}
      variant="tertiary"
    />
  );

  if (isIcon) {
    return (
      <Tooltip label={label} isTouchDevice={false} inPortal>
        {button}
      </Tooltip>
    );
  }

  return button;
};
